import {Component, LOCALE_ID, computed, effect, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { AuthService } from "./auth/auth.service";
import { VERSION_NUMBER } from "../environments/version.info";
import { Title } from "@angular/platform-browser";
import {LanguageService} from 'src/app/shared/language.service';
import { MatDialog } from "@angular/material/dialog";
import { TotpSetupComponent } from "./auth/totp-setup/totp-setup.component";
import { ConfigurationService } from "./configuration/configuration.service";
import { UsersService } from "./users/users.service";
import { TranslationSettingsService } from "./shared/translation-dialog/translation-settings.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit  {
  private readonly languageService = inject(LanguageService);
  private authService = inject(AuthService);
  private titleService = inject(Title);
  private configurationService = inject(ConfigurationService);
  private usersService = inject(UsersService);
  private translationSettingsService = inject(TranslationSettingsService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  protected localeId = inject(LOCALE_ID);

  // The user email + logout/language menu must stay hidden on the login
  // screen: Firebase can already have a signed-in user there (e.g. mid
  // TOTP-enrollment, right after the primary factor succeeds but before MFA
  // is verified), which would otherwise leak the account identity and a
  // working logout control onto an unauthenticated-looking screen.
  private readonly isLoginPage = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.startsWith('/login')),
      startWith(this.router.url.startsWith('/login')),
    ),
    { initialValue: this.router.url.startsWith('/login') },
  );

  title = "ServiceAdminApp";
  languageList = [
    { code: "en", label: "English", path: "./assets/icons/gb-flag.svg" },
    { code: "pl", label: "Polski", path: "./assets/icons/pl-flag.svg" },
  ];
  versionNumber = VERSION_NUMBER;

  readonly isTotpEnrolled = this.usersService.isTotpEnrolled;
  readonly ignoreMfa = this.usersService.ignoreMfa;
  private readonly config = toSignal(this.configurationService.config$, { initialValue: null });

  showMfaConfig = computed(() => {
    const config = this.config();
    if (!config?.configMap || this.ignoreMfa() || this.isTotpEnrolled()) {
      return false;
    }
    const configMap: Record<string, string> = config.configMap as any;
    const mfaEnabled = JSON.parse(configMap.security_enable_mfa ?? 'false');
    const mfaAuthAppEnabled = JSON.parse(configMap.security_mfa_auth_app ?? 'false');
    return mfaEnabled && mfaAuthAppEnabled;
  });

  constructor() {
    this.authService.autoAuth();
    console.log("AppComponent constructor");
    this.titleService.setTitle("Service Admin");

    // Rehydrate the persisted tenant language selection (Configuration ->
    // Languages) into TranslationSettingsService as soon as both the config
    // and the full language list are available, so "translate" dialogs
    // elsewhere in the app see it without requiring a visit to Configuration.
    effect(() => {
      const config = this.config();
      const languages = this.languageService.languages();
      if (config?.configMap && languages.length > 0) {
        const configMap: Record<string, string> = config.configMap as any;
        const selectedLocales = configMap.languages ? configMap.languages.split(",") : [];
        this.translationSettingsService.loadFromLocales(selectedLocales, languages);
      }
    });
  }

  ngOnInit(): void {
    this.languageService.loadLanguages().subscribe();
    this.usersService.checkTotpStatus();
  }

  get userName() {
    return this.authService.userName();
  }

  get showUserMenu() {
    return !this.isLoginPage();
  }

  signOut() {
    this.authService.logout();
  }

  openTotpConfiguration() {
    const dialogRef = this.dialog.open(TotpSetupComponent, {
      width: '420px',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.usersService.checkTotpStatus();
    });
  }
}
